/**
 * harness/postText.js
 * T-Cron-2026-10-24 02:00 早间 (round-1-of-day) — 改进清单 #8「批量操作」共用文案组装
 *
 * 为什么抽成共享模块:
 *   PostDetail 里已经有「标题 + 正文 + 标签」的文案组装逻辑,本轮新增的
 *   「批量复制」需要**同一套口径**。两份实现必然漂移:一处把标签写成
 *   `#a #b`,另一处写成 `a,b` ⇒ 用户复制出来的格式不一样,还得手动改。
 *   ⇒ 单一事实来源,PostDetail 与批量复制都调它。
 *
 * 文案口径(与 PostDetail 现有行为逐字等价,只是搬家):
 *   `${title}\n\n${body}\n\n${tagsLine}`   —— 标签行缺失时省略
 *   tagsLine = tags.map(t => `#${t}`).join(' ')
 *
 * 注意:本文件是 .js 且不含 JSX ⇒ vite.config.js 的 esbuild.jsxInject
 * 只对 .jsx 生效,这里不涉及 React,无需 import React。
 */

/**
 * 取某个版本的文案数据。找不到该版本时回退到选题自身的扁平字段。
 * @param {Object} post
 * @param {string} ver 版本键(A / B / C …)
 * @returns {Object|null}
 */
export function getVersionData(post, ver) {
  if (!post) return null;
  if (post.versions && post.versions[ver]) return post.versions[ver];
  return post;
}

/**
 * 解析「该用哪个版本」:
 *   1. 优先用筛选器指定的版本(preferred),前提是该版本真实存在;
 *   2. 否则用 A 版(与 PostDetail 的初始状态一致);
 *   3. 再否则用第一个可用版本。
 * @param {Object} post
 * @param {string} [preferred]
 * @returns {string} 版本键;没有版本时返回 ''
 */
export function resolveVersionKey(post, preferred) {
  if (!post) return '';
  const keys = post.versions ? Object.keys(post.versions).sort() : [];
  if (!keys.length) return '';
  if (preferred && keys.indexOf(preferred) !== -1) return preferred;
  if (keys.indexOf('A') !== -1) return 'A';
  return keys[0];
}

/**
 * 组装单篇的可复制文案。
 * @param {Object} post
 * @param {string} [preferred] 期望的版本键
 * @returns {string} 空串表示没有可复制内容
 */
export function buildCopyText(post, preferred) {
  if (!post) return '';
  const ver = resolveVersionKey(post, preferred);
  const vd = getVersionData(post, ver);
  if (!vd) return '';

  const title = vd.title || '';
  const body = vd.body || '';
  const tags = (vd.tags || []).map(t => `#${t}`).join(' ');
  if (!title && !body) return '';

  return `${title}\n\n${body}${tags ? `\n\n${tags}` : ''}`;
}

/**
 * 组装「一次复制全部版本」的文案(A/B/C 一起)。
 *
 * 🔴 为什么放在共享模块而不是 PostDetail 里就地拼:
 *   详情抽屉的「复制三版」按钮和未来的其它入口(比如批量复制全部版本)
 *   必须拼出**逐字一致**的文案 —— 两处各拼一次,改了一处忘了另一处,
 *   用户复制出来的 A/B/C 段落格式就会不一致,还得手动调。
 *   ⇒ 与 buildCopyText / buildBatchCopyText 同属一个「文案口径」家族,
 *   放在一起才能保证一致。
 *
 * 口径(与老仓库 App.jsx 的 handleCopyAll 逐字等价,只是搬家):
 *   《书名》 × 热点
 *   (空行)
 *   【A 标题】…
 *   【A 正文】…
 *   (空行)
 *   【B 标题】…
 *   【B 正文】…
 *   (空行)
 *   【C 标题】…
 *   【C 正文】…
 *   (空行)
 *   #AI辅助创作
 *
 * 标签行也带上(每版自己的标签)—— 老仓库那版没带标签,这里补上,
 * 因为「一键复制三版」的场景就是「直接粘进小红书编辑器」,标签是刚需。
 *
 * @param {Object} post
 * @returns {string} 空串表示该选题没有任何版本内容
 */
export function buildAllVersionsText(post) {
  if (!post) return '';
  const versions = post.versions && Object.keys(post.versions).length
    ? post.versions
    : { A: post };
  const keys = Object.keys(versions).sort();

  const blocks = [];
  for (const ver of keys) {
    const vd = versions[ver] || {};
    const title = vd.title || '';
    const body = vd.body || '';
    if (!title && !body) continue;   // 空版本不占位,免得粘出来一堆空标题
    const tags = (vd.tags || []).map(t => `#${t}`).join(' ');
    blocks.push(
      `【${ver} 标题】${title}\n【${ver} 正文】${body}${tags ? `\n${tags}` : ''}`
    );
  }
  if (!blocks.length) return '';

  const head = `《${post.book || ''}》${post.hotTopic ? ` × ${post.hotTopic}` : ''}`;
  return [head, '', ...blocks, '', '#AI辅助创作'].join('\n');
}

/**
 * 批量组装可复制文案(多篇拼接)。
 *
 * 分隔符用「———」而不是空行:空行在正文里本来就可能出现,用户粘到
 * 小红书编辑器里分不清哪一段结束、下一段开始。
 *
 * @param {Array} posts
 * @param {string} [preferred]
 * @returns {string}
 */
export function buildBatchCopyText(posts, preferred) {
  return (posts || [])
    .map(p => buildCopyText(p, preferred))
    .filter(t => !!t)
    .join('\n\n———\n\n');
}

/**
 * 统计「用户视角的文案字数」。
 *
 * 🔴 为什么不能直接用 String.length:
 *   `String.length` 数的是 UTF-16 code unit,不是「人读起来几个字」。
 *   对中文内容两条都差不多,但一旦正文里混进 **emoji / 生僻字 / 代理对**,
 *   `length` 会把它们算成 2 个字符 ⇒ 报出来的字数比实际多 ⇒ 用户按这个数字
 *   判断「会不会超小红书 1000 字上限」时会被误导。
 *
 * 口径(与老仓库 PostDetail 的 charCount 逐字等价):
 *   · CJK 汉字逐字算 1;
 *   · 连续的英文字母/数字算 1 个「词」(英文按词更贴近实际占用);
 *   · 其它字符(标点/空格/emoji)不计。
 *
 * @param {string} text
 * @returns {number}
 */
export function countCopyChars(text) {
  const s = String(text == null ? '' : text);
  if (!s) return 0;
  const cn = (s.match(/[一-龥]/g) || []).length;
  const en = (s.match(/[A-Za-z0-9]+/g) || []).length;
  return cn + en;
}

const PostText = {
  getVersionData,
  resolveVersionKey,
  buildCopyText,
  buildAllVersionsText,
  buildBatchCopyText,
  countCopyChars,
};

window.PostText = PostText;

export default PostText;
